import React from 'react';
import { Award, Sparkles, CheckCircle, BookOpen, MessageSquare, PenTool, AlertCircle } from 'lucide-react';
import type { UISuggestion, GrammarRubric } from '../../types/grammar';

const clampScore = (value: number) => Math.max(0, Math.min(100, value));

function ScoreBar({
  label,
  score,
  color,
  icon,
}: {
  label: string;
  score: number;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tighter">
        <div className="flex items-center gap-1.5">
          {icon}
          <span>{label}</span>
        </div>
        <span className="text-slate-700 dark:text-slate-300">{Math.round(score)}/100</span>
      </div>
      <div className="w-full bg-slate-200/50 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
        <div
          className={`${color} h-full rounded-full transition-all duration-500 ease-out shadow-sm`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

export type InsightsPanelProps = {
  rubric: GrammarRubric | null;
  suggestions: UISuggestion[];
  onHover?: (id: string | null) => void;
  /** When true, show "Correcting…" loading state in Corrections instead of empty/results */
  isChecking?: boolean;
};

export const InsightsPanel: React.FC<InsightsPanelProps> = ({
  rubric,
  suggestions,
  onHover,
  isChecking = false,
}) => {
  const hasResults = rubric != null;
  const n = suggestions.length;

  return (
    <div className="h-full min-h-0 flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto sm:max-h-[calc(100vh-220px)]">
        {/* Loading placeholder when checking and no results yet (avoids empty scorecard area) */}
        {isChecking && !hasResults && (
          <div className="px-4 py-6 border-b border-slate-200 dark:border-slate-800">
            <div className="flex flex-col items-center justify-center py-8 text-slate-500 dark:text-slate-400">
              <div className="rounded-full h-10 w-10 border-4 border-slate-200 dark:border-slate-700 border-t-indigo-600 dark:border-t-indigo-500 animate-spin mb-3" />
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Checking grammar…</p>
              <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">Analyzing writing…</p>
            </div>
          </div>
        )}

        {/* Results state: Scorecard + Teacher Note (upgraded visual) */}
        {hasResults && (
          <>
            <div className="p-4 sm:p-5 border-b border-indigo-100 dark:border-indigo-900/50 bg-gradient-to-br from-indigo-50/60 to-white dark:from-indigo-950/30 dark:to-slate-900">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-600 dark:bg-indigo-500 rounded-lg shadow-sm">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-widest">
                    Scorecard
                  </span>
                </div>
                <span className="text-3xl font-black text-indigo-600 dark:text-indigo-400 drop-shadow-sm">
                  {Math.round(clampScore(rubric.overallScore))}/100
                </span>
              </div>
              <div className="grid gap-4">
                <ScoreBar label="Grammar" score={clampScore(rubric.grammar)} color="bg-emerald-500" icon={<BookOpen className="w-3.5 h-3.5" />} />
                <ScoreBar label="Clarity" score={clampScore(rubric.clarity)} color="bg-sky-500" icon={<MessageSquare className="w-3.5 h-3.5" />} />
                <ScoreBar label="Vocabulary" score={clampScore(rubric.vocabulary)} color="bg-violet-500" icon={<PenTool className="w-3.5 h-3.5" />} />
              </div>
            </div>

            {/* Teacher's Note box */}
            <div className="mx-4 mb-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 border-l-4 border-l-indigo-400 dark:border-l-indigo-500 shadow-sm">
              <p className="text-xs font-bold text-indigo-900 dark:text-indigo-200 not-italic mb-1 uppercase tracking-wide">
                Teacher&apos;s Note
              </p>
              <p className="text-[13px] leading-relaxed italic text-slate-700 dark:text-slate-300 tamil-text whitespace-pre-wrap">
                &quot;{rubric.feedback ?? rubric.teacherNote ?? '—'}&quot;
              </p>
            </div>
          </>
        )}

        {/* Corrections section (always present) */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-200 dark:border-slate-800">
          <Sparkles className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            Corrections ({isChecking ? '…' : n})
          </span>
        </div>

        {isChecking ? (
          /* Loading state */
          <div className="flex flex-col items-center justify-center py-16 text-slate-500 dark:text-slate-400 min-h-[240px]">
            <div className="rounded-full h-10 w-10 border-4 border-slate-200 dark:border-slate-700 border-t-indigo-600 dark:border-t-indigo-500 animate-spin mb-4" />
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Correcting…</p>
            <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">Validating grammar, clarity and vocabulary…</p>
            <p className="text-xs mt-0.5 text-slate-400 dark:text-slate-500">Please wait…</p>
          </div>
        ) : n === 0 ? (
          /* Empty state — encouraging copy */
          <div className="flex flex-col items-center justify-center py-10 sm:py-16 text-slate-500 dark:text-slate-400 min-h-[240px]">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm sm:text-base font-semibold text-slate-700 dark:text-slate-300">No errors found!</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Great job with your writing.</p>
          </div>
        ) : (
          /* List state — preserve hover-to-highlight and focusedId flow */
          <div className="px-4 pb-4 space-y-4">
            {suggestions.map((item) => (
              <button
                key={item.id}
                type="button"
                onMouseEnter={() => onHover?.(item.id)}
                onMouseLeave={() => onHover?.(null)}
                className="w-full text-left p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 hover:border-indigo-400 dark:hover:border-indigo-600 hover:shadow-md transition-all cursor-default relative overflow-hidden"
              >
                <span className="absolute top-0 right-0 px-3 py-1 text-[9px] font-bold uppercase bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-bl-xl">
                  {item.category || 'GRAMMAR'}
                </span>
                <div className="flex flex-col gap-3 mt-1">
                  <div className="flex flex-col gap-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Mistake
                    </p>
                    <p className="text-rose-600 dark:text-rose-400 font-medium line-through decoration-2 decoration-rose-500/50 text-base">
                      {item.original}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Correction
                    </p>
                    <p className="text-emerald-600 dark:text-emerald-400 font-bold text-base flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 shrink-0" />
                      {item.suggestion}
                    </p>
                  </div>
                </div>
                {item.reason_tamil && (
                  <div className="mt-4 pt-3 border-t border-dashed border-slate-200 dark:border-slate-700">
                    <div className="flex gap-2 items-start">
                      <AlertCircle className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium tamil-text">
                        {item.reason_tamil}
                      </p>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsPanel;
